import { store } from "../store/store";
import axios from "axios";
import { resetUserSession } from '../../containers/login/loginSlice'
import { config } from "../utils/config";

let API_URL = "/api"
switch (config.env) {
    case "STAG":
        API_URL = "http://localhost:8089/api"
        break
    case "PROD":
        console.log = console.warn = console.error = () => { }
        API_URL = "https://v365.groupe-vital.com/api"
        break
    case "PREPROD":
        console.log = console.warn = console.error = () => { }
        API_URL = "https://ppdv365.groupe-vital.com/api"
        break
}

const extName = {
    'text/html': 'html',
    'text/plain': 'txt',
    'image/gif': 'gif',
    'image/jpeg': 'jpg',
    'image/jpg': 'jpg',
    'image/png': 'png',
    'image/svg+xml': 'svg',
    'audio/mp3': 'mp3',
    'audio/aac': 'aac',
    'audio/x-wav': 'wav',
    'video/mp4': 'mp4',
    'text/csv': 'csv',
    'application/msword': 'doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
        'docx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-powerpoint': 'ppt',
    'application/vnd.openxmlformats-officedocument.presentationml.presentation':
        'pptx',
    'application/pdf': 'pdf',
}

export default class NetworkManager {

    pendingRequests = []
    // Set once the first 401 has reset the session, so a burst of parallel
    // 401s triggers a single reset/reload instead of one per request.
    sessionResetting = false

    handleUnauthorized(doReload) {
        if (this.sessionResetting) return
        this.sessionResetting = doReload
        store.dispatch(resetUserSession({ doReload }))
    }

    static getInstance() {
        if (!this.instance) {
            this.instance = new NetworkManager()
        }
        return this.instance;
    }

    getBaseUrl() {
        return API_URL
    }

    getMediaStorageUrl() {
        return API_URL + "/v1/planning"
    }

    getUploadUrl() {
        return this.getMediaStorageUrl() + "/upload"
    }

    getDownloadUrl() {
        return this.getMediaStorageUrl() + "/downloadReport/"
    }

    /**
     * URL of a collab's profile picture, for use as an <img src>.
     *
     * Not fetched through postRequest/getRequest on purpose. Letting the browser load it as
     * an image is what buys the caching: the backend answers with a long-lived
     * `immutable` Cache-Control, so a face is fetched once and then comes from disk cache
     * on every later page load. Pulling the same bytes through axios into a data URL would
     * re-download all of them on every visit.
     *
     * `photoUpdatedAt` is what makes that safe to cache so aggressively — it changes when
     * the collab uploads a new picture, which changes the URL, so the stale entry is simply
     * never requested again. Passing null returns null: a collab with no picture has no URL
     * to load, and the caller renders initials instead of firing a request that 404s.
     *
     * @param {number|string} idCollab
     * @param {string|number|null} photoUpdatedAt `photo_updated_at` from a collab listing
     * @returns {string|null}
     */
    getCollabPhotoUrl(idCollab, photoUpdatedAt) {
        if (!idCollab || !photoUpdatedAt) return null
        // Numeric so the cache key stays stable regardless of how the timestamp was
        // serialized (listings return a SQL string, setMyPhoto returns one too, but a
        // caller holding a Date would otherwise produce a different URL for the same photo).
        const version = new Date(photoUpdatedAt).getTime()
        if (!version) return null
        return `${API_URL}${config.apiPaths.collabs.photo}/${idCollab}?v=${version}`
    }

    /**
     * 
     * @param {*} params 
     * @returns 
     */
    parseGetParams(params) {
        if (params) {
            let arrayOfParams = []
            for (let key in params) {
                arrayOfParams.push(key + "=" + params[key])
            }
            if (arrayOfParams.length)
                return "?" + arrayOfParams.join("&")
        }
        return ""
    }

    /**
     * 
     * @param {*} path 
     * @param {*} params 
     * @returns 
     */
    async postRequest(path, params) {

        const jsonParams = JSON.stringify(params)
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true
        }
        try {
            if (this.pendingRequests.includes(path)) {
                console.log("duplicate call !")
                console.log(path)
            } else {
                this.pendingRequests.push(path)
            }
            //console.log('POST REQUEST '+this.getBaseUrl() + path + " PARAMS: " + jsonParams)
            const response = await axios.post(this.getBaseUrl() + path, jsonParams, options)
            if (response.api_status === 401) {
                this.handleUnauthorized(path !== config.apiPaths.user.login)
            }
            return { responseData: response.data, body: params }
        } catch (error) {
            // axios sets `error.response` only when the server actually answered. A network
            // failure — server down, offline, DNS, timeout, a cancelled request — leaves it
            // undefined, and reading `.status` off it threw a TypeError from inside this catch.
            // That skipped the return below, so the caller never received `responseError` and
            // its own error handling never ran: the UI stayed silent at precisely the moment
            // the API was unreachable. Guarded, an unreachable API falls through to the
            // caller's generic failure path, which every caller already handles.
            if (error.response && error.response.status === 401) {
                this.handleUnauthorized(path !== config.apiPaths.user.login)
            }
            return { responseError: error.response || null }
        } finally {
            // In `finally`, not on the success path: the cleanup used to sit just before the
            // successful return, so a failed request left its path in `pendingRequests`
            // permanently. Every later call to that path then logged "duplicate call !", and
            // the duplicate guard was dead for it for the rest of the session.
            this.pendingRequests = this.pendingRequests.filter(p => p !== path)
        }
    }

    /**
     * 
     * @param {*} path 
     * @param {*} params 
     * @param {*} token 
     * @returns 
     */
    async getRequest(path, params, token) {
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json',
                'Authorization': `${token ? token : localStorage.getItem("AUTHORIZATION")}`
            },
            withCredentials: true
        }
        try {
            if (this.pendingRequests.includes(path)) {
                console.log("duplicate call !")
                console.log(path)
            } else {
                this.pendingRequests.push(path)
            }
            let url = this.getBaseUrl() + path + this.parseGetParams(params)
            const response = await axios.get(url, options)
            if (response.api_status === 401) {
                this.handleUnauthorized(path !== config.apiPaths.user.msAuth)
            }
            return { responseData: response.data }
        } catch (error) {
            // axios sets `error.response` only when the server actually answered. A network
            // failure — server down, offline, DNS, timeout, a cancelled request — leaves it
            // undefined, and reading `.status` off it threw a TypeError from inside this catch.
            // That skipped the return below, so the caller never received `responseError` and
            // its own error handling never ran: the UI stayed silent at precisely the moment
            // the API was unreachable. Guarded, an unreachable API falls through to the
            // caller's generic failure path, which every caller already handles.
            if (error.response && error.response.status === 401) {
                this.handleUnauthorized(path !== config.apiPaths.user.msAuth)
            }
            return { responseError: error.response || null }
        } finally {
            // In `finally`, not on the success path: the cleanup used to sit just before the
            // successful return, so a failed request left its path in `pendingRequests`
            // permanently. Every later call to that path then logged "duplicate call !", and
            // the duplicate guard was dead for it for the rest of the session.
            this.pendingRequests = this.pendingRequests.filter(p => p !== path)
        }
    }

    /**
     * 
     * @param {*} file 
     * @param {*} fileName 
     * @param {*} apiKey 
     * @returns 
     */
    async uploadFile(file, fileName, apiKey) {
        if (!file) return Promise.resolve(null)

        const base64Response = await fetch(file);
        const blob = await base64Response.blob();
        let fileType = blob.type
        if (fileType) fileName += "." + extName[fileType]
        var formData = new FormData();
        formData.append('file', blob, fileName)

        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'multipart/form-data',
                "api-key": apiKey,
            }
        }
        try {
            const response = await axios.post(this.getUploadUrl(), formData, options);
            if (response && response.data && response.data.filename) {
                return Promise.resolve(this.getDownloadUrl() + response.data.filename)
            }
            return Promise.resolve(null)
        } catch (error) {
            return Promise.resolve(null)
        }
    }

    /**
     * Uploads a PDF justificatif onto a leave.
     *
     * Separate from uploadAssets because it targets a different, permission-guarded store —
     * see LeaveAttachmentsController. id_planning rides in the query string because the
     * backend must authorise before multer parses the multipart body.
     */
    async uploadLeaveAttachment(file, idPlanning) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.planning.uploadLeaveAttachment}?id_planning=${idPlanning}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /**
     * URL of a leave attachment, for opening in a new tab. Same reasoning as
     * getCollabPhotoUrl — the browser fetches it directly. No cache-busting version here:
     * the response is sent `private, no-store`, so there is nothing to bust.
     */
    getLeaveAttachmentUrl(idAttachment) {
        if (!idAttachment) return null
        return `${this.getBaseUrl()}${config.apiPaths.planning.downloadLeaveAttachment}/${idAttachment}`
    }

    /**
     * Uploads a PDF justificatif onto an arrêt de travail's planning leave. The same store as
     * uploadLeaveAttachment, reached through the workStoppages routes so HR holding that
     * scope (rather than `leaves` edit on the team) can attach it. id_planning rides in the
     * query string for the same reason: the backend authorises before multer parses the body.
     */
    async uploadWorkStoppageAttachment(file, idPlanning) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.workStoppages.uploadAttachment}?id_planning=${idPlanning}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /** URL of an arrêt's justificatif, for opening in a new tab. See getLeaveAttachmentUrl. */
    getWorkStoppageAttachmentUrl(idAttachment) {
        if (!idAttachment) return null
        return `${this.getBaseUrl()}${config.apiPaths.workStoppages.downloadAttachment}/${idAttachment}`
    }

    /**
     * A collab's own justificatif, onto their pending arrêt request — the self-service twin of
     * uploadWorkStoppageAttachment, needing no scope because the API checks ownership instead.
     */
    async uploadMyWorkStoppageAttachment(file, idPlanning) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.workStoppages.uploadMyAttachment}?id_planning=${idPlanning}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /** URL of one of the collab's own justificatifs. See getLeaveAttachmentUrl. */
    getMyWorkStoppageAttachmentUrl(idAttachment) {
        if (!idAttachment) return null
        return `${this.getBaseUrl()}${config.apiPaths.workStoppages.downloadMyAttachment}/${idAttachment}`
    }

    /**
     * Uploads a CV (PDF or DOCX) onto a V-Reference contact.
     *
     * Separate from uploadAssets because it targets a permission-guarded store — see
     * VReferenceAttachmentsController. id_vreference rides in the query string because the
     * backend must authorise before multer parses the multipart body.
     */
    async uploadVReferenceAttachment(file, idVReference) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.vreference.uploadAttachment}?id_vreference=${idVReference}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /**
     * URL of a V-Reference CV, for opening in a new tab. Same reasoning as
     * getLeaveAttachmentUrl — the browser fetches it directly, and there is no cache-busting
     * version because the response is sent `private, no-store`.
     */
    getVReferenceAttachmentUrl(idAttachment) {
        if (!idAttachment) return null
        return `${this.getBaseUrl()}${config.apiPaths.vreference.downloadAttachment}/${idAttachment}`
    }

    /**
     * Uploads one file (image or PDF) onto a news post.
     *
     * Separate from uploadAssets because it targets a permission-guarded store — see
     * NewsAttachmentsController. id_news rides in the query string because the backend must
     * authorise before multer parses the multipart body.
     */
    async uploadNewsAttachment(file, idNews) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.dashboard.uploadNewsAttachment}?id_news=${idNews}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /**
     * URL of a news attachment, for use as an <img src> or opened in a new tab. Same
     * reasoning as getCollabPhotoUrl — the browser fetches it directly, which is what buys
     * the caching. No cache-busting version is needed: the bytes at a given id never change,
     * an edit is a new upload with a new id.
     */
    getNewsAttachmentUrl(idAttachment) {
        if (!idAttachment) return null
        return `${this.getBaseUrl()}${config.apiPaths.dashboard.downloadNewsAttachment}/${idAttachment}`
    }

    /**
     * Uploads one file onto a V-Learning lesson — a video, a PDF, a .pptx source, a document
     * or a caption track.
     *
     * `id_lesson` and `kind` ride in the query string for the same reason every other
     * attachment uploader in this file does it: the backend must authorise before multer
     * parses the multipart body and writes the bytes to disk.
     *
     * `onProgress` is optional and exists because a training video is the one upload in this
     * application large enough that a spinner is not an acceptable answer — the caller gets
     * a 0-100 number to render a real bar.
     */
    async uploadVLearnFile(file, idLesson, kind, onProgress) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }
        if (typeof onProgress === 'function') {
            options.onUploadProgress = (event) => {
                if (!event.total) return
                onProgress(Math.round((event.loaded * 100) / event.total))
            }
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.vlearn.filesUpload}`
            + `?id_lesson=${encodeURIComponent(idLesson)}&kind=${encodeURIComponent(kind)}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /** Uploads a course cover image. Same query-string reasoning as uploadVLearnFile. */
    async uploadVLearnCover(file, idCourse, onProgress) {
        if (!file) return Promise.reject("No file")

        const formData = new FormData()
        formData.append('file', file)

        const options = {
            headers: { 'lang': localStorage.getItem("lang") },
            withCredentials: true,
        }
        if (typeof onProgress === 'function') {
            options.onUploadProgress = (event) => {
                if (!event.total) return
                onProgress(Math.round((event.loaded * 100) / event.total))
            }
        }

        const url = `${this.getBaseUrl()}${config.apiPaths.vlearn.coverUpload}?id_course=${encodeURIComponent(idCourse)}`
        const response = await axios.post(url, formData, options)
        return response && response.data
    }

    /**
     * URL of a V-Learning lesson file, for an <iframe src> (PDF viewer) or an <a href>
     * (download). The browser fetches it directly, which is what makes a 40-page PDF render
     * in the platform's own viewer instead of arriving through Redux as a blob.
     */
    getVLearnFileUrl(idFile) {
        if (!idFile) return null
        return `${this.getBaseUrl()}${config.apiPaths.vlearn.filesDownload}/${idFile}`
    }

    /**
     * URL of an uploaded training video, for a `<video src>`.
     *
     * Deliberately a different endpoint from getVLearnFileUrl: this one answers 206 Partial
     * Content to the Range requests a `<video>` element makes when the learner seeks. Pointing
     * a player at the plain download URL would work, and would re-download the whole file on
     * every scrub.
     */
    getVLearnStreamUrl(idFile) {
        if (!idFile) return null
        return `${this.getBaseUrl()}${config.apiPaths.vlearn.filesStream}/${idFile}`
    }

    /**
     * URL of a course cover image.
     *
     * Takes the course's `updated_at` as a cache-buster, unlike the attachment URLs above:
     * a cover is replaced IN PLACE on the same course id, so the bytes at this URL really do
     * change and a browser holding the old ones would show the previous image indefinitely.
     */
    getVLearnCoverUrl(idCourse, updatedAt) {
        if (!idCourse) return null
        const base = `${this.getBaseUrl()}${config.apiPaths.vlearn.coverDownload}/${idCourse}`
        return updatedAt ? `${base}?v=${encodeURIComponent(updatedAt)}` : base
    }

    async uploadAssets(file, tableName, idParent, filename) {
        if (!file) return Promise.resolve(null);

        const formData = new FormData();
        formData.append('file', file);

        const options = {
            headers: {
                'lang': localStorage.getItem("lang")
            },
            withCredentials: true,
        };

        try {
            const url = `${this.getBaseUrl()}/v1/assets/upload/${tableName}/${idParent}/${filename}`;
            const response = await axios.post(url, formData, options);

            if (response && response.data) {
                return response.data;
            }
            return Promise.reject("File upload failed");
        } catch (error) {
            console.error("Error in uploadFile:", error);
            return Promise.reject(error);
        }
    }

    /**
     * 
     * @param {*} filename 
     * @returns 
     */
    async downloadFile(filename) {
        if (!filename) return Promise.resolve(null)

        const options = {
            headers: {
                'lang': localStorage.getItem("lang")
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.get(`${this.getDownloadUrl()}${filename}`, options)
            if (response && response.data) {
                var data = new Blob([response.data])
                var excelURL = window.URL.createObjectURL(data)
                let tempLink = document.createElement('a')
                tempLink.href = excelURL
                tempLink.setAttribute('download', filename)
                tempLink.click()
                return Promise.resolve(true)
            }
            return Promise.resolve(null)
        } catch (error) {
            return Promise.resolve(null)
        }
    }

    /**
     * Function to fetch and download a file from a post request
     * @param {*} path 
     * @param {*} params 
     * @param {*} filename 
     * @returns 
     */
    async fetchFile(path, params, filename) {
        const jsonParams = JSON.stringify(params)
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.post(this.getBaseUrl() + path, jsonParams, options)
            if (response && response.data) {
                var data = new Blob([response.data])
                var fileURL = window.URL.createObjectURL(data)
                let tempLink = document.createElement('a')
                tempLink.href = fileURL
                tempLink.setAttribute('download', filename)
                tempLink.click()
                return Promise.resolve(true)
            }
            return Promise.reject(response)
        } catch (error) {
            return Promise.reject(error)
        }
    }

    /**
     * function for downloading assets receiving the file creating a link for it and clicking it
     * @param {*} path 
     * @param {*} filename 
     * @returns 
     */
    async downloadAssetFile(path, filename) {
        const options = {
            headers: {
                'lang': localStorage.getItem("lang"),
                'Content-Type': 'application/json'
            },
            withCredentials: true,
            responseType: 'blob'
        }
        try {
            const response = await axios.get(this.getBaseUrl() + path, options);
            if (response && response.data) {
                var data = new Blob([response.data]);
                var fileURL = window.URL.createObjectURL(data);
                let tempLink = document.createElement('a');
                tempLink.href = fileURL;
                tempLink.setAttribute('download', filename);
                tempLink.click();
                return Promise.resolve(true);
            }
            return Promise.reject(response);
        } catch (error) {
            return Promise.reject(error);
        }
    }

}